/* eslint-disable jsdoc/require-jsdoc */
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { APIProvider, Map, useMap } from '@vis.gl/react-google-maps';
import {
  Alert,
  DashboardDataFilter,
  DeviceAttributes,
  DeviceAttributesV1,
  DeviceType,
  Group,
  ParametersType,
  ParticleData,
  TimeSpan,
  TimeSpanDateAttributes
} from '../../constants/telosair-interfaces';
import { Filters } from '../../services/localstorage.service';
import useStorageState from '../../hooks/useStorageState';
import { useUnifiedData } from '../../hooks/useUnifiedData';
import MapDeviceMarker, { PENTAGON_CLIP_PATH } from '../../components/MapDeviceMarker';
import DeviceDetailPanel from '../../components/DeviceDetailPanel';
import ParameterLegend from '../../components/ParameterLegend';
import FloorMapCarousel from '../../components/FloorMapCarousel';
import Loader from '../../components/loader';

interface MapPageProps {
  groups: Group[];
  dataFilter: DashboardDataFilter | null;
  setDataFilter: (filter: DashboardDataFilter | null) => void;
  loading: boolean;
  timeSpan: TimeSpan;
  timeSpanDate: TimeSpanDateAttributes | null;
}

type DeviceHoverState = {
  x: number;
  y: number;
  deviceData: DeviceAttributesV1;
  particleData: ParticleData;
  alert: Alert[];
};

/**
 * Checks whether a lat/lng pair can be plotted.
 * @param lat - Latitude.
 * @param lng - Longitude.
 * @returns True when both are real, in range, and not the 0,0 placeholder.
 */
function isPlottable(lat: unknown, lng: unknown): lat is number {
  return (
    typeof lat === 'number' &&
    typeof lng === 'number' &&
    !Number.isNaN(lat) &&
    !Number.isNaN(lng) &&
    !(lat === 0 && lng === 0) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
}

/** A device placed on the map, with where it goes and how it is drawn. */
export type PlottedDevice = {
  device: DeviceAttributesV1;
  position: google.maps.LatLngLiteral;
  indoor: boolean;
};

/**
 * Works out every device that can go on the world map.
 *
 * A device's own latitude/longitude (set in Add/Edit Device) wins. Without
 * one, an outdoor group's x/y still count as lng/lat, as they always have.
 * Indoor groups' x/y are floor-plan pixels and are never used here. Devices in
 * no group are included when they have their own location.
 * @param groups - All groups with their devices.
 * @param devices - The client's device list (carries deviceType and location).
 * @returns Devices to plot, each once.
 */
export function collectPlottedDevices(
  groups: Group[],
  devices: DeviceAttributes[]
): PlottedDevice[] {
  // A plain object: `Map` in this file is the Google Maps component.
  const bySerial: Record<number, DeviceAttributes | undefined> = {};
  devices.forEach((d) => {
    bySerial[d.serialNumber] = d;
  });
  const seen = new Set<number>();
  const result: PlottedDevice[] = [];

  const isIndoor = (serial: number, groupIsIndoor: boolean): boolean => {
    const type = bySerial[serial]?.deviceType;
    return type ? type === DeviceType.INDOOR : groupIsIndoor;
  };

  groups.forEach((group) => {
    const groupIsOutdoor = group.floorPlan === DeviceType.OUTDOOR;
    group.sensorDetailsList?.forEach((device) => {
      if (seen.has(device.serial_number)) return;
      const own = bySerial[device.serial_number];
      const lat = device.latitude ?? own?.latitude;
      const lng = device.longitude ?? own?.longitude;
      let position: google.maps.LatLngLiteral | null = null;
      if (isPlottable(lat, lng)) {
        position = { lat, lng: lng as number };
      } else if (groupIsOutdoor && isPlottable(device.y, device.x)) {
        position = { lat: device.y, lng: device.x };
      }
      if (!position) return;
      seen.add(device.serial_number);
      result.push({ device, position, indoor: isIndoor(device.serial_number, !groupIsOutdoor) });
    });
  });

  devices.forEach((own) => {
    if (seen.has(own.serialNumber) || !isPlottable(own.latitude, own.longitude)) return;
    seen.add(own.serialNumber);
    const indoor = own.deviceType === DeviceType.INDOOR;
    // Not in any group, so there is no group row to show; build the fields the
    // marker and detail panel read.
    const device = {
      id: own.id,
      serial_number: own.serialNumber,
      deviceName: own.deviceName,
      type: own.type,
      x: own.longitude,
      y: own.latitude,
      latitude: own.latitude,
      longitude: own.longitude,
      floorPlan: indoor ? DeviceType.INDOOR : DeviceType.OUTDOOR,
      tags: own.tags
    } as unknown as DeviceAttributesV1;
    result.push({
      device,
      position: { lat: own.latitude as number, lng: own.longitude as number },
      indoor
    });
  });

  return result;
}

// Geographic center of the contiguous United States — used as the default
// viewport so the map opens on the US before fitting to any device markers.
const US_CENTER = { lat: 39.8283, lng: -98.5795 };
const US_ZOOM = 4;

type FullScreenMapProps = {
  devices: PlottedDevice[];
  selectedDevice: number | null;
  displayParameter: ParametersType | null;
  setDataFilter: (filter: DashboardDataFilter | null) => void;
  timeSpan: TimeSpan;
  timeSpanDate: TimeSpanDateAttributes | null;
};

/**
 * Renders every plottable device on a single full-screen Google Map and keeps
 * all markers in view by fitting the map bounds to them. Clicking a marker
 * opens a device detail side panel.
 * @param props - Props for the full-screen map.
 * @returns A React element with the rendered map and markers.
 */
function FullScreenMap(props: FullScreenMapProps): ReactElement {
  const { devices, selectedDevice, displayParameter, setDataFilter, timeSpan, timeSpanDate } =
    props;
  const map = useMap();
  const [hovered, setHovered] = useState<DeviceHoverState | null>(null);

  useEffect(() => {
    if (!map || devices.length === 0) return;
    const bounds = new window.google.maps.LatLngBounds();
    devices.forEach(({ position }) => {
      bounds.extend(position);
    });
    map.fitBounds(bounds, 64);
    // Avoid an over-zoomed map when only a single device is plotted.
    if (devices.length === 1) {
      const listener = window.google.maps.event.addListenerOnce(map, 'idle', () => {
        if ((map.getZoom() ?? 0) > 17) map.setZoom(17);
      });
      // eslint-disable-next-line consistent-return
      return () => window.google.maps.event.removeListener(listener);
    }
  }, [map, devices]);

  return (
    <>
      <Map
        mapId="2710c5f22fa0158b"
        colorScheme="LIGHT"
        defaultCenter={US_CENTER}
        defaultZoom={US_ZOOM}
        gestureHandling="greedy"
        scrollwheel
        draggable
        zoomControl
        mapTypeControl={false}
        streetViewControl={false}
        fullscreenControl={false}
        onClick={() => setHovered(null)}
      >
        {devices.map(({ device, position, indoor }) => (
          <MapDeviceMarker
            key={device.serial_number}
            deviceData={device}
            position={position}
            indoor={indoor}
            selectedDevice={selectedDevice}
            displayParameter={displayParameter}
            onSelect={setHovered}
            setDataFilter={setDataFilter}
          />
        ))}
      </Map>
      {hovered && (
        <DeviceDetailPanel
          device={hovered.deviceData}
          initialParameter={displayParameter}
          timeSpan={timeSpan}
          timeSpanDate={timeSpanDate || { startDate: 0, endDate: 0 }}
          onClose={() => setHovered(null)}
        />
      )}
    </>
  );
}

/**
 * Full-screen Map view for the dashboard. Aggregates every device that carries
 * geographic coordinates across all groups and plots them on one large map.
 * @param props - Props for the map page.
 * @returns A React element representing the full-screen map view.
 */
export default function MapPage(props: MapPageProps): ReactElement {
  const { groups, dataFilter, setDataFilter, loading, timeSpan, timeSpanDate } = props;
  const { parameters, devices } = useUnifiedData();
  const [selectedParameters, setSelectedParameters] = useStorageState<ParametersType[]>(
    Filters.dashboardFlooMapParameters,
    []
  );

  const selectedDevice = dataFilter?.type === 'device' ? dataFilter.id : null;
  // Device opened from an indoor floor plan (the fallback below), shown in the
  // same detail panel the world-map markers open.
  const [floorPlanDevice, setFloorPlanDevice] = useState<DeviceAttributesV1 | null>(null);

  const plottableDevices = useMemo(
    () => collectPlottedDevices(groups, devices || []),
    [groups, devices]
  );
  const hasIndoorOnMap = plottableDevices.some((d) => d.indoor);
  const hasOutdoorOnMap = plottableDevices.some((d) => !d.indoor);

  // Indoor groups carry a floor-plan image and pixel positions rather than
  // lat/lng, so they can still be drawn - just not on a world map.
  const indoorGroups = useMemo(
    () =>
      groups.filter(
        (group) =>
          group.floorPlan !== DeviceType.OUTDOOR && (group.sensorDetailsList?.length ?? 0) > 0
      ),
    [groups]
  );

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

  // A marker badge can only carry one reading, so the legend behaves as a
  // single-select here: the first (and only) selected parameter drives every
  // marker, and an empty selection means "All" — i.e. the default AQI badge.
  const displayParameter: ParametersType | null = selectedParameters[0] ?? null;
  const displayParameterLabel =
    parameters.find((p) => p.parameterName === displayParameter)?.shortName ?? null;

  const parameterClass = (value: ParametersType): string =>
    displayParameter === value ? 'text-white' : 'text-gray-400';

  /**
   * Selects the parameter shown on every marker, or clears back to the default
   * when the active parameter is clicked again.
   * @param selectedValue - The parameter being toggled.
   */
  const handleParameterToggle = (selectedValue: string): void => {
    setSelectedParameters(
      displayParameter === selectedValue ? [] : [selectedValue as ParametersType]
    );
  };

  if (loading) {
    return (
      <div className="relative flex h-full w-full flex-1 items-center justify-center rounded-md border border-gray-300 bg-container-600 dark:border-background-700">
        <Loader />
      </div>
    );
  }

  const hasDevices = plottableDevices.length > 0;
  const showFloorPlans = !hasDevices && indoorGroups.length > 0;

  // Nothing is geo-located, but the client does have indoor groups: fall back to
  // the floor-plan carousel the Simple view uses rather than stranding the user
  // on an empty world map. Its own legend and group navigation come with it.
  if (showFloorPlans) {
    const indoorDeviceCount = indoorGroups.reduce(
      (total, group) => total + (group.sensorDetailsList?.length ?? 0),
      0
    );
    return (
      <div className="relative flex h-full w-full flex-1 flex-col overflow-hidden rounded-md border border-gray-300 bg-container-600 dark:border-background-700">
        {/* Bottom-left: the carousel owns the top bar (legend toggle) and the
            vertically centred navigation arrows. */}
        <div className="pointer-events-none absolute bottom-3 left-3 z-[2] rounded-lg bg-container-900/80 px-3 py-1.5 text-sm font-medium text-gray-200 backdrop-blur-sm">
          <span className="text-primary-300">{indoorDeviceCount}</span> Device
          {indoorDeviceCount !== 1 ? 's' : ''} on floor plans
          <span className="mx-1.5 text-gray-500">·</span>
          <span className="text-gray-400">No map locations set</span>
        </div>
        <FloorMapCarousel
          groups={indoorGroups}
          loading={false}
          dataFilter={dataFilter}
          setDataFilter={setDataFilter}
          onDeviceClick={setFloorPlanDevice}
        />
        {floorPlanDevice && (
          <DeviceDetailPanel
            device={floorPlanDevice}
            initialParameter={displayParameter}
            timeSpan={timeSpan}
            timeSpanDate={timeSpanDate || { startDate: 0, endDate: 0 }}
            onClose={() => setFloorPlanDevice(null)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="relative flex h-full w-full flex-1 overflow-hidden rounded-md border border-gray-300 bg-container-600 dark:border-background-700">
      {/* Parameter legend overlay (only relevant when markers are plotted) */}
      {hasDevices && (
        <div className="absolute left-3 top-3 z-[1] flex max-w-[70%] flex-wrap gap-2 rounded-lg bg-container-900/80 p-2 backdrop-blur-sm">
          <ParameterLegend
            handleParameterClick={() => setSelectedParameters([])}
            name="All"
            parameter={'all' as ParametersType}
            active={displayParameter === null}
            className={parameterClass('all' as ParametersType)}
          />
          {parameters.map((parameter) => (
            <ParameterLegend
              key={parameter.parameterName}
              handleParameterClick={handleParameterToggle}
              name={parameter.shortName}
              parameter={parameter.parameterName}
              active={displayParameter === parameter.parameterName}
              className={parameterClass(parameter.parameterName)}
            />
          ))}
        </div>
      )}

      {/* Device count badge + the metric the marker badges are showing */}
      <div className="absolute right-3 top-3 z-[1] rounded-lg bg-container-900/80 px-3 py-1.5 text-sm font-medium text-gray-200 backdrop-blur-sm">
        <span className="text-primary-300">{plottableDevices.length}</span> Devices on map
        {hasDevices && (
          <>
            <span className="mx-1.5 text-gray-500">·</span>
            <span className="text-primary-300">{displayParameterLabel ?? 'AQI'}</span>
          </>
        )}
      </div>

      {/* Shape key, only when both kinds are on the map */}
      {hasIndoorOnMap && hasOutdoorOnMap && (
        <div className="absolute bottom-6 left-3 z-[1] flex items-center gap-3 rounded-lg bg-container-900/80 px-3 py-1.5 text-xs text-gray-300 backdrop-blur-sm">
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-3 w-3 rounded-full bg-gray-300" /> Outdoor
          </span>
          <span className="flex items-center gap-1.5">
            <span
              className="inline-block h-3 w-3 bg-gray-300"
              style={{ clipPath: PENTAGON_CLIP_PATH }}
            />{' '}
            Indoor
          </span>
        </div>
      )}

      {/* No-location hint, shown over the default US map */}
      {!hasDevices && (
        <div className="pointer-events-none absolute left-1/2 top-3 z-[1] -translate-x-1/2 rounded-lg bg-container-900/80 px-3 py-1.5 text-center text-sm text-gray-300 backdrop-blur-sm">
          No devices with map locations yet
        </div>
      )}

      <APIProvider apiKey={apiKey}>
        <FullScreenMap
          devices={plottableDevices}
          selectedDevice={selectedDevice}
          displayParameter={displayParameter}
          setDataFilter={setDataFilter}
          timeSpan={timeSpan}
          timeSpanDate={timeSpanDate}
        />
      </APIProvider>
    </div>
  );
}
